import { useState, useEffect, useRef } from 'react';
import { toast } from '../../../Toaster/toast';
import AdminPane from '../../AdminPane';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faLockOpen } from '@fortawesome/free-solid-svg-icons';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import { PrismBanner, PrismSwitch, PrismBadge, PrismButton } from '@prism-ui/react';
import useFetch from '../../../../hooks/useFetch';
import Loader from '../../../Loader';
import ErrorMessage from '../../../ErrorMessage';
import PermissionLevel from '../../../../data/PermissionLevel';

type ChapterPermission = {
  id: number;
  chapter_id: number;
  tcm_can_view: boolean;
  chapter: {
    id: number;
    location: string;
    distro: string;
  };
};

type Props = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  currentUser: any;
};

export function ApplicationPermissions({ currentUser }: Props) {
  const { data, loading, error } = useFetch<ChapterPermission[]>('/api/chapter_application_permissions/all');
  const [permissions, setPermissions] = useState<ChapterPermission[]>([]);
  const [unlockedChapterId, setUnlockedChapterId] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const isAdmin = currentUser?.Role?.permission_level >= PermissionLevel.Admin;
  const canEdit = (chapterId: number) =>
    isAdmin || chapterId === currentUser?.chapter_id;

  useEffect(() => {
    if (data) setPermissions(data);
  }, [data]);

  // Re-lock when user clicks outside the pane
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setUnlockedChapterId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLockToggle = (chapterId: number) => {
    setUnlockedChapterId(prev => (prev === chapterId ? null : chapterId));
  };

  const handleToggle = async (chapterId: number, newValue: boolean) => {
    const permission = permissions.find(p => p.chapter_id === chapterId);
    if (!permission) return;
    const chapterName = permission.chapter.location;

    // Optimistic update
    setPermissions(prev =>
      prev.map(p => (p.chapter_id === chapterId ? { ...p, tcm_can_view: newValue } : p))
    );

    try {
      await axios.put(`/api/chapter_application_permissions/${chapterId}`, { tcm_can_view: newValue });
      if (newValue) {
        toast.success(`Application access for ${chapterName} is now ON. TCMs can view and rank applications.`);
      } else {
        toast.success(`Application access for ${chapterName} is now OFF. TCMs will not be able to access applications.`);
      }
    } catch {
      // Revert on failure
      setPermissions(prev =>
        prev.map(p => (p.chapter_id === chapterId ? { ...p, tcm_can_view: !newValue } : p))
      );
      toast.error(`Failed to update ${chapterName} permissions. Please try again.`);
    }
  };

  if (loading) return <Loader />;
  if (error) return <ErrorMessage error="Failed to load application permissions" />;

  return (
    <AdminPane width="narrow">
      <div ref={containerRef}>
        <PrismBanner tone="info" layout="inline">
          Officers always have access to view and rank applications for their chapter by default.
          This setting only controls TCM (Triad Committee Member) access to applications.
        </PrismBanner>

        {/* `w-auto` keeps the table to its content width instead of stretching the columns
            across the full card, which left the status badge marooned from the chapter name. */}
        <table className="table table-sm clr:mt-6 clr:w-auto">
          <thead>
            <tr>
              <th>Chapter</th>
              <th className="clr:text-center">Unlock to Edit</th>
              <th className="clr:text-center">TCM Access</th>
              <th className="clr:text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            {permissions.map(perm => {
              const editable = canEdit(perm.chapter_id);
              const isUnlocked = unlockedChapterId === perm.chapter_id;
              return (
                <tr key={perm.chapter_id} style={!editable ? { opacity: 0.5 } : undefined}>
                  <td>{perm.chapter.location}</td>
                  <td className="clr:text-center">
                    {editable && (
                      <PrismButton
                        size="sm"
                        display="outline"
                        tone="info"
                        title={isUnlocked ? 'Lock row' : 'Unlock row to edit'}
                        onPrismClick={() => handleLockToggle(perm.chapter_id)}
                      >
                        <FontAwesomeIcon icon={isUnlocked ? faLockOpen : faLock} />
                      </PrismButton>
                    )}
                  </td>
                  <td className="clr:text-center">
                    <PrismSwitch
                      label={`TCM access for ${perm.chapter.location}`}
                      hideLabel={true}
                      checked={perm.tcm_can_view}
                      disabled={!isUnlocked || !editable}
                      onPrismChange={(e: CustomEvent<{ checked: boolean }>) => {
                        if (isUnlocked && editable) handleToggle(perm.chapter_id, e.detail.checked);
                      }}
                    />
                  </td>
                  <td className="clr:text-center">
                    <PrismBadge tone={perm.tcm_can_view ? 'success' : 'danger'}>
                      {perm.tcm_can_view ? 'ON' : 'OFF'}
                    </PrismBadge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </AdminPane>
  );
}
